import { Menu, Transition } from "@headlessui/react";
import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  DocumentDuplicateIcon,
  PencilSquareIcon,
  Square3Stack3DIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";
import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateTestDeckPopup } from "../../../../components/create-test-deck-popup";
import { DeleteChartConfirmDialog } from "../../../../components/dialogs/delete-chart-confirm-dialog";
import { useAuth } from "../../../../contexts/AuthContext";
import { ChartListResponse, ChartStatusColorEnum, ChartStatusEnum, ChartTypeEnum } from "../../../../models/chart";
import { ArrowPathIcon } from '@heroicons/react/24/outline';

type AdminChartDisplayProps = {
  chart: ChartListResponse;
  getDate: (date: string) => string;
  deleteChart: (id: string) => void;
  editTestDeck: (id: string) => void;
  downloadChart: (chartId: string, type: "download" | "print") => Promise<void>;
  downloadTestDeck: (chartId: string) => Promise<void>;
  duplicateChart: (id: string) => void;
  backgroundColor: string;
  regenerateChart: (id: string) => void;
  regenerateTestDeck: (id: string) => void;
  headerLength: number;
};

const AdminChartDisplay = (props: AdminChartDisplayProps) => {
  const [isCreateTestDeckOpen, setIsCreateTestDeckOpen] =
    useState<boolean>(false);
  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] =
    useState<boolean>(false);
  const {
    chart,
    deleteChart,
    duplicateChart,
    editTestDeck,
    downloadChart,
    downloadTestDeck,
    backgroundColor,
    regenerateChart,
    regenerateTestDeck,
  } = props;
  const navigate = useNavigate();
  const auth = useAuth();

  return (
    <>
      <div
        className={`flex items-center w-full justify-items-center py-2 ${backgroundColor}`}
      >
        <div className={`grid w-full grid-cols-${props.headerLength} p-3 ml-1 text-sm font-poppins justify-items-start`}>
          {chart.electionType.id === ChartTypeEnum.PRESIDENTIAL_PRIMARY &&
            <p className="font-semibold">{chart?.politicalParty?.abbv ?? ''}</p>
          }
          <p className="font-semibold">{chart.precinct}</p>
          <p className="font-semibold text-primary-blue">
            {chart.municipality}
          </p>
          <p>{chart.county}</p>
          <p>{props.getDate(chart.electionDate)}</p>
          <p>{props.getDate(chart.modifiedAt)}</p>
          <div className="flex items-start gap-2">
            <p
              className={`${ChartStatusColorEnum[
                chart.status as keyof typeof ChartStatusColorEnum
              ]
                } px-1 rounded-sm uppercase text-xs`}
            >
              {chart.status === "InProgress" ? "In Progress" : chart.status}
            </p>
            {chart.fullChartDownloadLink && (
              <p className="px-1 text-xs text-blue-700 uppercase bg-blue-100 rounded-sm">
                Test Deck
              </p>
            )}
          </div>
          <Menu as="div" className="relative inline-block text-left">
            <div>
              <Menu.Button className="inline-flex justify-center w-full p-1 text-sm border rounded-md outline-none border-primary-blue text-primary-blue">
                Actions
                <ChevronDownIcon
                  className="w-5 h-5 ml-2 -mr-1 outline-none text-primary-blue hover:text-primary-blue"
                  aria-hidden="true"
                />
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 w-48 p-2 mt-4 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none hover:cursor-pointer">
                <div className="text-xs">
                  {chart.downloadLink && (
                    <Menu.Item>
                      {({ active }) => (
                        <div className="flex justify-center text-center">
                          <ArrowDownTrayIcon
                            className={`${active
                              ? "bg-primary-blue text-white border-1 border-primary-blue"
                              : "text-primary-blue"
                              } h-10 w-10 p-2 rounded-l-md`}
                            aria-hidden="true"
                          />
                          <span
                            onClick={() => downloadChart(chart.id, "download")}
                            className={`${active
                              ? "bg-primary-blue text-white"
                              : "text-primary-blue"
                              } group flex rounded-r-md items-center w-full text-md`}
                          >
                            Download Chart
                          </span>
                        </div>
                      )}
                    </Menu.Item>
                  )}
                  {auth.isAdmin && chart.downloadLink && (
                    <Menu.Item>
                      {({ active }) => (
                        <div className="flex justify-center text-center">
                          <ArrowDownTrayIcon
                            className={`${active
                              ? "bg-primary-blue text-white border-1 border-primary-blue"
                              : "text-primary-blue"
                              } h-10 w-10 p-2 rounded-l-md`}
                            aria-hidden="true"
                          />
                          <span
                            onClick={() => downloadChart(chart.id, "print")}
                            className={`${active
                              ? "bg-primary-blue text-white"
                              : "text-primary-blue"
                              } group flex rounded-r-md items-center w-full text-md`}
                          >
                            Download Print Chart
                          </span>
                        </div>
                      )}
                    </Menu.Item>
                  )}
                  {auth.isAdmin && chart.fullChartDownloadLink && (
                    <Menu.Item>
                      {({ active }) => (
                        <div className="flex justify-center text-center">
                          <ArrowDownTrayIcon
                            className={`${active
                              ? "bg-primary-blue text-white border-1 border-primary-blue"
                              : "text-primary-blue"
                              } h-10 w-10 p-2 rounded-l-md`}
                            aria-hidden="true"
                          />
                          <span
                            onClick={() => downloadTestDeck(chart.id)}
                            className={`${active
                              ? "bg-primary-blue text-white"
                              : "text-primary-blue"
                              } group flex rounded-r-md items-center w-full text-md`}
                          >
                            Download Test Deck
                          </span>
                        </div>
                      )}
                    </Menu.Item>
                  )}
                  <Menu.Item>
                    {({ active }) => (
                      <div className="flex justify-center text-center">
                        <Square3Stack3DIcon
                          className={`${active
                            ? "bg-primary-blue text-white border-1 border-primary-blue"
                            : "text-primary-blue"
                            } h-10 w-10 p-2 rounded-l-md`}
                          aria-hidden="true"
                        />
                        <span
                          onClick={() =>
                            chart.hasBallotFile
                              ? editTestDeck(chart.id)
                              : setIsCreateTestDeckOpen(true)
                          }
                          className={`${active
                            ? "bg-primary-blue text-white"
                            : "text-primary-blue"
                            } group flex rounded-r-md items-center w-full text-md`}
                        >
                          {chart.hasBallotFile ? "Edit" : "Create"} Test Deck
                        </span>
                      </div>
                    )}
                  </Menu.Item>
                  {chart.status === ChartStatusEnum.Completed &&
                    chart.hasBallotFile &&
                    <Menu.Item>
                      {({ active }) => (
                        <div className="flex justify-center text-center">
                          <ArrowPathIcon
                            className={`${active
                              ? "bg-primary-blue text-white border-1 border-primary-blue"
                              : "text-primary-blue"
                              } h-10 w-10 p-2 rounded-l-md`}
                            aria-hidden="true"
                          />
                          <span
                            onClick={() =>
                              regenerateTestDeck(chart.id)
                            }
                            className={`${active
                              ? "bg-primary-blue text-white"
                              : "text-primary-blue"
                              } group flex rounded-r-md items-center w-full text-md`}
                          >
                            Regenerate Test Deck
                          </span>
                        </div>
                      )}
                    </Menu.Item>}
                  {chart.status === ChartStatusEnum.Completed && <Menu.Item>
                    {({ active }) => (
                      <div className="flex justify-center text-center">
                        <ArrowPathIcon
                          className={`${active
                            ? "bg-primary-blue text-white border-1 border-primary-blue"
                            : "text-primary-blue"
                            } h-10 w-10 p-2 rounded-l-md`}
                          aria-hidden="true"
                        />
                        <span
                          onClick={() =>
                            regenerateChart(chart.id)
                          }
                          className={`${active
                            ? "bg-primary-blue text-white"
                            : "text-primary-blue"
                            } group flex rounded-r-md items-center w-full text-md`}
                        >
                          Regenerate Chart
                        </span>
                      </div>
                    )}
                  </Menu.Item>}
                  <Menu.Item>
                    {({ active }) => (
                      <div className="flex justify-center text-center">
                        <DocumentDuplicateIcon
                          className={`${active
                            ? "bg-primary-blue text-white border-1 border-primary-blue"
                            : "text-primary-blue"
                            } h-10 w-10 p-2 rounded-l-md`}
                          aria-hidden="true"
                        />
                        <span
                          onClick={() => duplicateChart(chart.id)}
                          className={`${active
                            ? "bg-primary-blue text-white"
                            : "text-primary-blue"
                            } group flex rounded-r-md items-center w-full text-md`}
                        >
                          Duplicate Chart
                        </span>
                      </div>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <div className="flex justify-center text-center">
                        <PencilSquareIcon
                          className={`${active
                            ? "bg-primary-blue text-white border-1 border-primary-blue"
                            : "text-primary-blue"
                            } h-10 w-10 p-2 rounded-l-md`}
                          aria-hidden="true"
                        />
                        <span
                          onClick={() => {
                            navigate("edit/" + chart.id);
                          }}
                          className={`${active
                            ? "bg-primary-blue text-white"
                            : "text-primary-blue"
                            } group flex rounded-r-md items-center w-full text-md`}
                        >
                          Edit
                        </span>
                      </div>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <div className="flex justify-center text-center">
                        <TrashIcon
                          className={`${active
                            ? "bg-primary-blue text-white border-1 border-primary-blue"
                            : "text-primary-blue"
                            } h-10 w-10 p-2 rounded-l-md`}
                          aria-hidden="true"
                        />
                        <span
                          onClick={() => setDeleteConfirmationOpen(true)}
                          className={`${active
                            ? "bg-primary-blue text-white"
                            : "text-primary-blue"
                            } group flex rounded-r-md items-center w-full text-md`}
                        >
                          Delete
                        </span>
                      </div>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
        <div></div>
      </div>
      <DeleteChartConfirmDialog
        isOpen={isDeleteConfirmationOpen}
        onDelete={() => deleteChart(chart.id)}
        onClose={() => setDeleteConfirmationOpen(false)}
      />
      <CreateTestDeckPopup
        chartId={chart.id}
        open={isCreateTestDeckOpen}
        close={() => setIsCreateTestDeckOpen(false)}
      />
    </>
  );
};

export default AdminChartDisplay;
